import React, { useState, useEffect } from "react";
import { Target, Shield, Sparkles } from "lucide-react";
import img from "../../../constant";
import Navbar from "../../../components/Navbar2";
import { useNavigate } from "react-router-dom";

interface Particle {
  id: number;
  x: number;
  y: number;
  size: number;
  delay: number;
  duration: number;
}

interface MousePosition {
  x: number;
  y: number;
}

const HeroNew: React.FC = () => {
  const navigate = useNavigate();
  const [mousePosition, setMousePosition] = useState<MousePosition>({
    x: 0,
    y: 0,
  });
  const [particles, setParticles] = useState<Particle[]>([]);

  useEffect(() => {
    const newParticles: Particle[] = Array.from({ length: 80 }, (_, i) => ({
      id: i,
      x: Math.random() * 500 - 250,
      y: Math.random() * 400 - 200,
      size: Math.random() * 4 + 2,
      delay: Math.random() * 2,
      duration: Math.random() * 3 + 2,
    }));
    setParticles(newParticles);
  }, []);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePosition({
      x: (e.clientX - rect.left - rect.width / 2) / 20,
      y: (e.clientY - rect.top - rect.height / 2) / 20,
    });
  };

  return (
    <div className="md:min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-cyan-50 overflow-hidden font-abel">
      <Navbar title="Login" link="/login" />

      <div className="relative mx-auto pt-10" onMouseMove={handleMouseMove}>
        <h1 className="text-3xl md:text-8xl leading-[109.9%] font-bold text-slate-800 md:mb-10 mb-9 text-center">
          Schedule interviews{" "}
          <span className="bg-gradient-to-r from-primary to-blue-700 bg-clip-text text-transparent">
            10X faster
          </span>
        </h1>

        <div className="relative md:h-96 h-64 max-w-7xl mx-auto flex items-center justify-center px-6">
          {/* Particle Figure */}
          <img src={img.crystalline} alt="icon" />
          <div
            className="relative w-64 h-96 transition-transform duration-300 ease-out"
            style={{
              transform: `translate(${mousePosition.x}px, ${mousePosition.y}px)`,
            }}
          >
            {particles.map((particle) => (
              <div
                key={particle.id}
                className="absolute bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full"
                style={{
                  left: `${particle.x + 10}px`,
                  top: `${particle.y + 200}px`,
                  width: `${particle.size}px`,
                  height: `${particle.size}px`,
                  animation: `float ${particle.duration}s ease-in-out ${particle.delay}s infinite`,
                  boxShadow: "0 0 10px rgba(6, 182, 212, 0.5)",
                }}
              />
            ))}
          </div>

          {/* Mobile Interface Card */}
          <div
            className="absolute left-1/2 top-1/2 transition-transform duration-300 ease-out"
            style={{
              transform: `translate(calc(-50% + ${
                -mousePosition.x * 0.5
              }px + 128px), calc(-50% + ${-mousePosition.y * 0.5}px))`,
            }}
          ></div>

          {/* Stats Cards */}
          <div
            className="absolute md:right-40 right-3 md:top-20 top-4 transition-transform duration-300 ease-out"
            style={{
              transform: `translate(${-mousePosition.x * 0.8}px, ${
                -mousePosition.y * 0.8
              }px)`,
              animation: "float-card 3s ease-in-out infinite",
            }}
          >
            <div className="bg-gradient-to-br from-cyan-500 to-blue-600 text-white flex items-center justify-center gap-2 md:px-6 px-4 md:py-4 py-3 rounded-2xl shadow-xl">
              <div className="md:text-3xl font-bold">90%</div>
              <div className="text-sm">Faster Shortlisting</div>
            </div>
          </div>

          <div
            className="absolute md:right-40 right-3 md:bottom-40 bottom-32 transition-transform duration-300 ease-out"
            style={{
              transform: `translate(${-mousePosition.x * 0.8}px, ${
                -mousePosition.y * 0.8
              }px)`,
              animation: "float-card 3s ease-in-out 0.5s infinite",
            }}
          >
            <div className="bg-gradient-to-br from-cyan-400 to-teal-500 text-white flex items-center justify-center gap-2 md:px-6 px-4 md:py-4 py-3 rounded-2xl shadow-xl">
              <div className="md:text-3xl font-bold">68%</div>
              <div className="text-sm">Cost Reduction</div>
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="w-full bg-white pb-10 mt-4 md:mt-8 px-3">
          <div className="grid grid-cols-3 md:gap-3 gap-2 mb-4 pt-6 max-w-7xl mx-auto">
            <div className="bg-white md:p-5 p-3 rounded-2xl shadow-md border md:border-2 md:border-primary border-gray-300 text-center hover:shadow-md transition-shadow">
              <div className="text-center group">
                <div className="w-14 h-14 mx-auto mb-4 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                  <Sparkles className="w-8 h-8 text-cyan-600" />
                </div>
                <h3 className="font-semibold text-slate-800 mb-1">
                  25th AI Recruiter
                </h3>
                <p className="text-sm text-slate-600">
                  Virtual Screening & Interviews
                </p>
              </div>
            </div>

            <div className="bg-white md:p-5 p-3 rounded-2xl shadow-md border md:border-2 md:border-primary border-gray-300 text-center hover:shadow-md transition-shadow">
              <div className="text-center group">
                <div className="w-14 h-14 mx-auto mb-4 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                  <Target className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="font-semibold text-slate-800 mb-1">
                  25th AI Direct
                </h3>
                <p className="text-sm text-slate-600">
                  End-to-end Sourcing & Benchmarking
                </p>
              </div>
            </div>

            <div className="bg-white md:p-5 p-3 rounded-2xl shadow-md border md:border-2 md:border-primary border-gray-300 text-center hover:shadow-md transition-shadow">
              <div className="text-center group">
                <div className="w-14 h-14 mx-auto mb-4 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                  <Shield className="w-8 h-8 text-indigo-600" />
                </div>
                <h3 className="font-semibold text-slate-800 mb-1">
                  25th Console
                </h3>
                <p className="text-sm text-slate-600">Secure & Manage Data</p>
              </div>
            </div>
          </div>

          <div className="text-center pt-3">
            <button
              onClick={() => navigate("/request")}
              className="border-2 border-primary bg-primary text-white rounded-full font-semibold hover:border-blue-500 hover:bg-white hover:text-primary hover:shadow-lg transition-all transform hover:scale-105"
            >
              <div className="px-6 py-3 border-2 border-white rounded-full h-full w-full flex justify-center items-center">
                Book a Demo to View Pricing
              </div>
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translate(0, 0);
            opacity: 0.6;
          }
          50% {
            transform: translate(0, -20px);
            opacity: 1;
          }
        }

        @keyframes float-card {
          0%, 100% {
            transform: translate(0, 0);
          }
          50% {
            transform: translate(0, -15px);
          }
        }

        @keyframes bounce-slow {
          0%, 100% {
            transform: translate(-50%, -50%) translateY(0);
          }
          50% {
            transform: translate(-50%, -50%) translateY(-10px);
          }
        }
      `}</style>
    </div>
  );
};

export default HeroNew;
